import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Collapse,
  Divider,
  Fab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import { pagesList, mobilePagesList } from '@/constants';
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ProfileSection } from './ProfileSection';
import PageTitleHeader from './PageTitleHeader';
import { useAppContext } from '@/context/context';
import LayoutTheme from './HoverTheme';

const APP_BAR_HEIGHT = 69;

type ExtendedPage = {
  primary?: string;
  icon?: JSX.Element;
  link?: string;
  children?: ExtendedPage[];
  isHeader?: boolean;
  isKedmaHeader?: boolean;
  isMessengersHeader?: boolean;
  isMobilePrimary?: boolean;
};

interface MobileDashboardLayoutProps {
  children: React.ReactNode;
}

const MobileDashboardLayout = ({ children }: MobileDashboardLayoutProps) => {
  const [currentPage, setCurrentPage] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSectionIdx, setOpenSectionIdx] = useState<number | null>(null);

  const { userPermission } = useAppContext();
  const theme = useTheme();
  const navigate = useNavigate();

  const filteredPagesList = pagesList.filter(page => {
    if (userPermission?.isAdmin) {
      return true;
    }

    const hasChildren = page.children && page.children.length > 0;

    const hasPermission = !hasChildren
      ? !page.link || userPermission?.hasPagePermission(page?.link || '')
      : page?.children?.some(child => !child.link || userPermission?.hasPagePermission(child?.link || ''));

    return hasPermission;
  });

  // Mobile pages - use mobilePagesList from constants
  const mobilePages = mobilePagesList.slice(0, 4).map(page => ({
    primary: page.primary,
    link: page.link || '',
    icon: page.icon
  }));

  return (
    <Stack sx={{ height: '100vh', overflow: 'hidden' }}>
      <Box
        sx={{
          py: 1,
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mb: `${APP_BAR_HEIGHT - 10}px`,
          zIndex: 0
        }}
      >
        <Box sx={{ flexShrink: 0, my: 1 }}>
          <PageTitleHeader />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'scroll', pl: 0.5 }}>
          {children}
        </Box>
      </Box>

      <Collapse in={isMenuOpen}>
        <Box
          sx={{
            height: '100vh',
            overflow: 'hidden',
            backgroundColor: 'primary.main',
            mt: '-100px',
            zIndex: 1,
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 900,
              height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
              overflowY: 'auto',
              // Hide scrollbar for Webkit browsers
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              // Hide scrollbar for Firefox
              scrollbarWidth: 'none',
              // Hide scrollbar for IE, Edge
              msOverflowStyle: 'none'
            }}
          >
            <List>
              {/* גריד 2 עמודות של קטגוריות ראשיות */}
              <Box
                sx={{
                  px: 2,
                  pt: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  alignItems: 'stretch'
                }}
              >
                {/* Group pages into pairs */}
                {Array.from({ length: Math.ceil(filteredPagesList.length / 2) }, (_, groupIndex) => {
                  const startIdx = groupIndex * 2;
                  const page1 = filteredPagesList[startIdx];
                  const page2 = filteredPagesList[startIdx + 1];
                  const isAnyOpen =
                    openSectionIdx !== null && (openSectionIdx === startIdx || openSectionIdx === startIdx + 1);

                  return (
                    <Box
                      key={groupIndex}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 2,
                        justifyItems: 'center',
                        alignItems: 'start'
                      }}
                    >
                      {/* Render both page cards using map */}
                      {[page1, page2].map((page, pairIndex) => {
                        if (!page) return null;

                        const pageIdx = startIdx + pairIndex;
                        const isOpen = openSectionIdx === pageIdx;

                        return (
                          <Box
                            key={pairIndex}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                              alignItems: 'center'
                            }}
                          >
                            <Box
                              onClick={() => {
                                if (!page.children && page.link) {
                                  navigate(page.link);
                                  setIsMenuOpen(false);
                                } else {
                                  setOpenSectionIdx(openSectionIdx === pageIdx ? null : pageIdx);
                                }
                              }}
                              sx={{
                                width: '25vw',
                                maxWidth: 80,
                                aspectRatio: '1 / 1',
                                borderRadius: 0.5,
                                border: theme =>
                                  `0.5px solid ${isOpen ? theme.palette.secondary.main : theme.palette.common.white}`,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: 'common.white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                p: 0.5
                              }}
                            >
                              <Box
                                sx={{
                                  color: `${theme.palette.common.white} !important`,
                                  mb: 1,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '18px !important',
                                    color: isOpen ? 'secondary.main !important' : 'common.white !important',
                                    transition: 'color 0.2s ease'
                                  },
                                  '& svg, & svg path': {
                                    fill: theme => (isOpen ? theme.palette.secondary.main : theme.palette.common.white),
                                    transition: 'fill 0.2s ease'
                                  }
                                }}
                              >
                                {page.icon}
                              </Box>
                              <Box
                                sx={{
                                  fontSize: 14,
                                  textAlign: 'center',
                                  color: isOpen ? 'secondary.main' : 'common.white'
                                }}
                              >
                                {page.primary}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Children section - spans full width under both cards */}
                      {isAnyOpen && (
                        <Box
                          sx={{
                            gridColumn: '1 / -1',
                            width: '100%'
                          }}
                        >
                          <Collapse in={isAnyOpen} timeout="auto">
                            <Box
                              sx={{
                                width: '90%',
                                p: 2,
                                mx: 'auto'
                              }}
                            >
                              {/* Show children from the open page */}
                              {filteredPagesList[openSectionIdx]?.children?.map(
                                (child: ExtendedPage, childIndex: number) => {
                                  // הצגת כותרות
                                  if (child.isHeader) {
                                    return (
                                      <Box key={childIndex} sx={{ mb: 2 }}>
                                        <LayoutTheme type="logo" />
                                      </Box>
                                    );
                                  }
                                  if (child.isKedmaHeader) {
                                    return (
                                      <Box key={childIndex} sx={{ mb: 2 }}>
                                        <LayoutTheme type="kedma" />
                                      </Box>
                                    );
                                  }
                                  if (child.isMessengersHeader) {
                                    return (
                                      <Box key={childIndex} sx={{ mb: 2 }}>
                                        <LayoutTheme type="messengers" />
          </Box>
                                    );
                                  }

                                  // הצגת פריטי תפריט רגילים
                                  if (!child.primary) return null;

                                  return (
                                    <Box key={childIndex}>
                                      <ListItem sx={{ py: 0.2 }}>
                                        <ListItemButton
                                          onClick={() => {
                                            navigate(child.link || '');
                                            setIsMenuOpen(false);
                                          }}
                                          sx={{ py: 0.5 }}
                                        >
                                          <ListItemIcon
                                            sx={{
                                              color: `${theme.palette.common.white} !important`,
                                              minWidth: 24,
                                              '& .MuiSvgIcon-root': {
                                                color: `${theme.palette.common.white} !important`,
                                                fontSize: '16px'
                                              },
                                              '& svg, & svg path': {
                                                fill: `${theme.palette.common.white} !important`
                                              }
                                            }}
                                          >
                                            {child.icon}
                                          </ListItemIcon>
                                          <ListItemText
                                            sx={{
                                              color: 'common.white'
                                            }}
                                            primary={child.primary}
                                            primaryTypographyProps={{ fontSize: 14 }}
                                          />
                    </ListItemButton>
                  </ListItem>
                                      <Divider sx={{ borderColor: 'common.white', borderWidth: 0.5 }} />
                                    </Box>
                                  );
                                }
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ mt: 6 }}>
                <ProfileSection onNavigate={() => setIsMenuOpen(false)} />
              </Box>
          </List>
          </Box>
        </Box>
      </Collapse>

      <BottomNavigation
        sx={{
          position: 'fixed',
          top: 'auto',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'secondary.light',
          borderRadius: 0,
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          height: APP_BAR_HEIGHT
        }}
        showLabels
        value={currentPage}
        onChange={(event, newValue) => {
          setCurrentPage(newValue);
          navigate(newValue);
          setIsMenuOpen(false);
        }}
      >
        {[...mobilePages.slice(0, 2), { primary: '', link: '', icon: null }, ...mobilePages.slice(2, 4)].map(
          (item, index) => {
            return <BottomNavigationAction key={index} label={item.primary} value={item.link} icon={item.icon} />;
          }
        )}
      </BottomNavigation>
      <Fab
        aria-label="toggle-menu"
        sx={{
          position: 'fixed',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          border: theme => `1px solid ${theme.palette.primary.main}`
        }}
        onClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
      >
        <Box
          component="span"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isMenuOpen ? (
            <RemoveOutlinedIcon fontSize="large" sx={{ color: 'primary.main' }} />
          ) : (
            <AddIcon fontSize="large" sx={{ color: 'primary.main' }} />
          )}
        </Box>
      </Fab>
    </Stack>
  );
};

export default MobileDashboardLayout;
